import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'

type MatchReportSummary = {
  jobId: string
  timestamp: string
  title: string
  employer: string
  matchCount: number
  topMatch?: string
  createdAt: string
}

function formatDate(dateString: string): string {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid date'
  return date.toLocaleDateString()
}

export default function HrReports() {
  const navigate = useNavigate()
  const [reports, setReports] = useState<MatchReportSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.jobs.list().then((res) => {
      const hrJobs = res.data.filter((j) => j.type === 'Listed' || j.type === 'Exclusive')
      Promise.all(
        hrJobs.map(async (job) => {
          try {
            const matchRes = await api.jobs.matches.getLatest(job.id)
            return {
              jobId: job.id,
              title: matchRes.data.title || job.title,
              employer: matchRes.data.employer || job.employer,
              matchCount: matchRes.data.matches.length,
              topMatch: matchRes.data.matches[0]?.name,
              createdAt: matchRes.data.created_at,
              timestamp: 'latest',
            }
          } catch {
            return null
          }
        })
      ).then((results) => {
        const validReports = results.filter((r): r is NonNullable<typeof r> => r !== null) as MatchReportSummary[]
        setReports(validReports)
        setLoading(false)
      })
    }).catch((err) => {
      setError(err.message)
      setLoading(false)
    })
  }, [])

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading tracking-tactical">MATCH REPORTS</h1>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {loading ? (
        <p className="text-text-muted text-sm">Loading reports...</p>
      ) : reports.length === 0 ? (
        <div className="border border-dashed border-border-subtle p-12 text-center">
          <p className="text-text-muted text-sm">No match reports yet. Run matching on a job to generate reports.</p>
        </div>
      ) : (
        <div className="border border-border-subtle">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left text-xs text-text-muted tracking-tactical py-3 px-4 font-normal">JOB</th>
                <th className="text-left text-xs text-text-muted tracking-tactical py-3 px-4 font-normal">EMPLOYER</th>
                <th className="text-left text-xs text-text-muted tracking-tactical py-3 px-4 font-normal">CANDIDATES</th>
                <th className="text-left text-xs text-text-muted tracking-tactical py-3 px-4 font-normal">TOP MATCH</th>
                <th className="text-left text-xs text-text-muted tracking-tactical py-3 px-4 font-normal">RUN</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr
                  key={`${r.jobId}-${r.timestamp}`}
                  className="border-b border-border-subtle hover:bg-surface-low transition-colors cursor-pointer"
                  onClick={() => navigate(`/match-reports/${r.jobId}`)}
                >
                  <td className="py-3 px-4 font-medium">{r.title}</td>
                  <td className="py-3 px-4 text-text-muted">{r.employer}</td>
                  <td className="py-3 px-4">
                    <Badge variant="required">{r.matchCount} found</Badge>
                  </td>
                  <td className="py-3 px-4 text-text-muted">
                    {r.topMatch || <span className="text-text-muted">—</span>}
                  </td>
                  <td className="py-3 px-4 text-text-muted text-xs">
                    {formatDate(r.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" className="text-xs">VIEW →</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
